import { useState } from "react";
import FlowPageView from "./FlowPageView";
import useEvaApi, { EvaApiCall } from "../../../hooks/useEvaApi";
import useCustomEffect from "../../../hooks/useCustomEffect";
import { useParams } from "react-router-dom";

function FlowPage() {
  const callEvaApi = useEvaApi();
  const { flow } = useParams();
  const [flowData, setFlowData] = useState({
    name: "",
    steps: [
      {
        id: "start",
        type: "start",
        position: { x: 0, y: 0 },
      },
      {
        id: "process_data",
        type: "process_data",
        position: { x: 0, y: 200 },
      },
    ],
    connections: [],
  });
  const [relations, setRelations] = useState({
    fields: { fetched: false, records: [] },
    files: { fetched: false, records: [] },
    employees: { fetched: false, records: [] },
    message_templates: { fetched: false, records: [] },
    questionnaires: { fetched: false, records: [] },
    parking_locations: { fetched: false, records: [] },
    salto_resources: { fetched: false, data: [] },
    salto_config: { fetched: false, data: null },
  });
  const [submitError, setSubmitError] = useState(null);

  /**
   * UseEffect for initializations.
   */
  useCustomEffect(() => {
    if (flow) {
      fetchFlowData();
    }
  });

  /**
   * Fetches the relation records based on the relation name.
   * @param {string} relation
   */
  async function fetchRelation(relation) {
    switch (relation) {
      case "fields":
        fetchFields();
        break;
      case "files":
        fetchFiles();
        break;
      case "employees":
        fetchEmployees();
        break;
      case "message_templates":
        fetchMessageTemplates();
        break;
      case "questionnaires":
        fetchQuestionnaires();
        break;
      case "parking_locations":
        fetchParkingLocations();
        break;
      case "salto_resources":
        fetchSaltoResources();
        break;
      case "salto_config":
        fetchSaltoConfig();
        break;
      default:
        break;
    }
  }

  /**
   * This method will fetch the fields.
   */
  async function fetchFields() {
    if (relations.fields.fetched) return;

    await callEvaApi(
      new EvaApiCall("fields")
        .setLoadingGroup("flow_page_fetch")
        .setRedirectOnError(false)
        .setOnSuccess((response) => {
          setRelations((prev) => ({
            ...prev,
            fields: { fetched: true, records: response.data.data.records },
          }));
        }),
    );
  }

  /**
   * This method will fetch the files.
   */
  async function fetchFiles() {
    if (relations.files.fetched) return;

    await callEvaApi(
      new EvaApiCall("files")
        .setLoadingGroup("flow_page_fetch")
        .setParams({
          where: [{ column: "type", values: ["houserule"] }],
        })
        .setRedirectOnError(false)
        .setOnSuccess((response) => {
          setRelations((prev) => ({
            ...prev,
            files: { fetched: true, records: response.data.data.records },
          }));
        }),
    );
  }

  /**
   * This method will fetch the employees.
   */
  async function fetchEmployees() {
    if (relations.employees.fetched) return;

    await callEvaApi(
      new EvaApiCall("employees")
        .setLoadingGroup("flow_page_fetch")
        .setRedirectOnError(false)
        .setOnSuccess((response) => {
          setRelations((prev) => ({
            ...prev,
            employees: { fetched: true, records: response.data.data.records },
          }));
        }),
    );
  }

  /**
   * This method will fetch the message templates.
   */
  async function fetchMessageTemplates() {
    if (relations.message_templates.fetched) return;

    await callEvaApi(
      new EvaApiCall("message_templates")
        .setLoadingGroup("flow_page_fetch")
        .setRedirectOnError(false)
        .setOnSuccess((response) => {
          setRelations((prev) => ({
            ...prev,
            message_templates: {
              fetched: true,
              records: response.data.data.records,
            },
          }));
        }),
    );
  }

  /**
   * This method will fetch the questionnaires.
   */
  async function fetchQuestionnaires() {
    if (relations.questionnaires.fetched) return;

    await callEvaApi(
      new EvaApiCall("questionnaires")
        .setLoadingGroup("flow_page_fetch")
        .setRedirectOnError(false)
        .setOnSuccess((response) => {
          setRelations((prev) => ({
            ...prev,
            questionnaires: {
              fetched: true,
              records: response.data.data.records,
            },
          }));
        }),
    );
  }

  /**
   * This method will fetch the parking locations.
   */
  async function fetchParkingLocations() {
    if (relations.parking_locations.fetched) return;

    await callEvaApi(
      new EvaApiCall("parking_locations")
        .setLoadingGroup("flow_page_fetch")
        .setRedirectOnError(false)
        .setOnSuccess((response) => {
          setRelations((prev) => ({
            ...prev,
            parking_locations: {
              fetched: true,
              records: response.data.data.records,
            },
          }));
        }),
    );
  }

  /**
   * This method will fetch the salto resources
   */
  async function fetchSaltoResources() {
    if (relations.salto_resources.fetched) return;

    await callEvaApi(
      new EvaApiCall("integrations/salto/resources")
        .setLoadingGroup("flow_page_fetch")
        .setRedirectOnError(false)
        .setOnSuccess((response) => {
          setRelations((prev) => ({
            ...prev,
            salto_resources: {
              fetched: true,
              data: response.data.data,
            },
          }));
        }),
    );
  }

  /**
   * This method will fetch the Salto config.
   */
  async function fetchSaltoConfig() {
    if (relations.salto_config.fetched) return;

    await callEvaApi(
      new EvaApiCall("integrations/salto/configs")
        .setLoadingGroup("flow_page_fetch")
        .setParams({
          with_salto_config_locks_data: true,
        })
        .setRedirectOnError(false)
        .setOnSuccess((response) => {
          setRelations((prev) => ({
            ...prev,
            salto_config: { fetched: true, data: response.data.data },
          }));
        }),
    );
  }

  /**
   * This method will fetch the flow data.
   */
  async function fetchFlowData() {
    await callEvaApi(
      new EvaApiCall(`flows/${flow}`)
        .setLoadingGroup("flow_page_fetch")
        .setParams({
          with_flow_connections_data: true,
          with_flow_steps_data: true,
          with_flow_step_fields_data: true,
          with_flow_step_messages_data: true,
        })
        .setRedirectOnError(true)
        .setOnSuccess((response) => {
          setFlowData({
            name: response.data.data.name,
            steps: response.data.data.steps.map((step) => ({
              id: step.id,
              type: step.type,
              position: step.position,
              proceed_on_error: step.proceed_on_error,
              force_show: step.force_show,
              houserules_expiration: step.houserules_expiration,
              file_id: step.file,
              questionnaire_id: step.questionnaire,
              questionnaire_expiration: step.questionnaire_expiration,
              link_to_houserules: step.link_to_houserules,
              salto_lock_id: step.salto_lock,
              reference_field_id: step.reference_field,
              fields: step.fields.map((row) =>
                row.map((stepField) => ({
                  field_id: stepField.field,
                  default_field: stepField.default_field,
                  required_backoffice: stepField.required_backoffice,
                  hidden_backoffice: stepField.hidden_backoffice,
                  required_terminal: stepField.required_terminal,
                  hidden_terminal: stepField.hidden_terminal,
                  hidden_outlook: stepField.hidden_outlook,
                  hidden_portal: stepField.hidden_portal,
                  required_portal: stepField.required_portal,
                  readonly_portal: stepField.readonly_portal,
                  condition_key: stepField.condition_key,
                  condition_value: stepField.condition_value,
                  condition_effect: stepField.condition_effect,
                })),
              ),
              messages: step.messages.map((message) => ({
                message_template_id: message.message_template,
                type: message.type,
                receiver: message.receiver,
                employee_id: message.employee,
                event: message.event,
              })),
              parking_locations: step.parking_locations,
            })),
            connections: response.data.data.connections.map((connection) => ({
              id: connection.id,
              from_step_id: connection.from_step,
              from_step_position: connection.from_step_position,
              to_step_id: connection.to_step,
              to_step_position: connection.to_step_position,
            })),
          });
        }),
    );
  }

  return (
    <FlowPageView
      relations={relations}
      fetchRelation={fetchRelation}
      flowData={flowData}
      setFlowData={setFlowData}
      submitError={submitError}
      setSubmitError={setSubmitError}
    />
  );
}

export default FlowPage;
